/**
 * Single write path for in-app History API navigation, and the popstate repair
 * that keeps the Back button in sync with `usePathname`.
 *
 * Next patches `pushState` / `replaceState` only after the app-router effect.
 * A raw `null` write before that patch stores a history entry without `__NA`.
 * Next then ignores Back (`state == null`) or reloads (`state` without `__NA`).
 * After the patch, passing `null` is required: the patch copies `__NA` and
 * syncs `usePathname`. Passing a state that already has `__NA` skips that sync.
 *
 * On Back, an entry without `__NA` is marked in place, without copying the
 * flight tree of the page being left. A fresh popstate then lets Next restore
 * from the address the browser already opened.
 */

interface NextMarkedHistoryState {
  readonly __NA: true;
  readonly __PRIVATE_NEXTJS_INTERNALS_TREE?: unknown;
}

/** `true` when the history state carries the Next app-router marker. */
export function isNextMarkedState(
  state: unknown,
): state is NextMarkedHistoryState {
  return (
    typeof state === "object" &&
    state !== null &&
    (state as { readonly __NA?: unknown }).__NA === true
  );
}

/**
 * State argument for a history write.
 *
 * After Next's patch: `null`, unless `extra` fields must ride on the entry.
 * Before the patch: the current state, so a write cannot wipe `__NA`.
 */
export function chooseRawWriteState(
  isPatched: boolean,
  currentState: unknown,
): unknown {
  return isPatched ? null : currentState;
}

/**
 * History state for a write that also stores app fields (overlay id, meeting flag).
 *
 * `keepNextMarker` is for a same-URL push (mobile overlay). Dropping `__NA`
 * there makes Next restore immediately and the overlay treats that as Back.
 * A URL change drops `__NA` so the patch copies the marker and syncs the view.
 */
export function historyStateForAppWrite(
  isPatched: boolean,
  currentState: unknown,
  extra: Readonly<Record<string, unknown>> | null,
  keepNextMarker: boolean,
): unknown {
  if (extra == null) return chooseRawWriteState(isPatched, currentState);
  const base: Record<string, unknown> =
    typeof currentState === "object" && currentState !== null
      ? { ...(currentState as Record<string, unknown>) }
      : {};
  for (const key of Object.keys(extra)) {
    base[key] = extra[key];
  }
  if (isPatched && !keepNextMarker) {
    // biome-ignore lint/performance/noDelete: key absence is what Next checks, not undefined.
    delete base.__NA;
    // biome-ignore lint/performance/noDelete: key absence is what Next checks, not undefined.
    delete base.__PRIVATE_NEXTJS_INTERNALS_TREE;
  }
  return base;
}

/** Unmarked Back: sync the URL Next already landed on. Marked: let Next handle it. */
export function shouldSyncUnmarkedPopstate(state: unknown): boolean {
  return !isNextMarkedState(state);
}

let guardInstalled = false;
/** Stops the repaired popstate from being treated as another unmarked Back. */
let syncingPopstate = false;

/**
 * Marks the entry the browser already moved to, without a flight tree.
 * Next then restores from `window.location` and keeps the current tree.
 * A copied tree from the page being left would show that page again.
 */
function repairedPopstateState(state: unknown): Record<string, unknown> {
  const repaired: Record<string, unknown> =
    typeof state === "object" && state !== null
      ? { ...(state as Record<string, unknown>) }
      : {};
  repaired.__NA = true;
  // biome-ignore lint/performance/noDelete: key absence is what Next checks, not undefined.
  delete repaired.__PRIVATE_NEXTJS_INTERNALS_TREE;
  return repaired;
}

function isHistoryMethodPatched(method: "pushState" | "replaceState"): boolean {
  return window.history[method] !== History.prototype[method];
}

function writeAppHistory(
  method: "pushState" | "replaceState",
  href: string,
  extra: Readonly<Record<string, unknown>> | null,
  keepNextMarker: boolean,
): void {
  window.history[method](
    historyStateForAppWrite(
      isHistoryMethodPatched(method),
      window.history.state,
      extra,
      keepNextMarker,
    ),
    "",
    href,
  );
}

function handlePopstate(event: PopStateEvent): void {
  if (syncingPopstate) return;
  if (!shouldSyncUnmarkedPopstate(event.state)) return;
  // Next reloads when the entry has no marker, and ignores a null state.
  // Stop that, mark the entry the browser already opened, then let Next and
  // the app's own Back listeners (overlays, help) see a normal popstate.
  event.stopImmediatePropagation();
  const repaired = repairedPopstateState(event.state);
  syncingPopstate = true;
  try {
    History.prototype.replaceState.call(
      window.history,
      repaired,
      "",
      window.location.href,
    );
    window.dispatchEvent(new PopStateEvent("popstate", { state: repaired }));
  } finally {
    syncingPopstate = false;
  }
}

/**
 * Registers the Back repair. Idempotent and SSR-safe. Module scope, not
 * useEffect, so the listener is registered before Next's app-router effect.
 */
export function installNextHistoryMarkerGuard(): void {
  if (typeof window === "undefined") return;
  if (guardInstalled) return;
  guardInstalled = true;
  window.addEventListener("popstate", handlePopstate);
}

/** In-app navigation: `pushState`, without destroying a Next marker. */
export function pushAppHistoryUrl(href: string): void {
  writeAppHistory("pushState", href, null, false);
}

/** In-app navigation: `replaceState`, without destroying a Next marker. */
export function replaceAppHistoryUrl(href: string): void {
  writeAppHistory("replaceState", href, null, false);
}

/**
 * `pushState` that also stores app fields on the history entry.
 * `keepNextMarker` only for a same-URL entry (overlay). A changed URL must
 * leave the marker for Next's patch to copy.
 */
export function pushAppHistoryExtras(
  href: string,
  extra: Readonly<Record<string, unknown>>,
  keepNextMarker = false,
): void {
  writeAppHistory("pushState", href, extra, keepNextMarker);
}
